// <tiptap-editor> replaces Trix for ActionText fields.
//   data-mode="collab"  persisted record: Yjs + Hocuspocus, saved automatically
//   data-mode="form"    new record: plain editor writing HTML to a hidden input
import { Editor, getMarkRange } from "@tiptap/core"
import Collaboration from "@tiptap/extension-collaboration"
import CollaborationCaret from "@tiptap/extension-collaboration-caret"
import { HocuspocusProvider } from "@hocuspocus/provider"
import * as Y from "yjs"
import { richTextExtensions } from "./rich_text_extensions.js"

// start_realtime.sh listens 2000 above the Rails port.
const REALTIME_PORT_OFFSET = 2000
const ANONYMOUS = { name: "…", color: "#999999" }

// A single click opens a link, but the first click of a double click must not.
const DOUBLE_CLICK_GRACE_MS = 250

const DEFAULT_LINK_COLOR = "#0000ee"

const providers = new Set()
const dirtyFormEditors = new Set()

const TOOLBAR = [
  { name: "bold", label: "B", title: "Bold", run: (c) => c.toggleBold(), active: "bold" },
  { name: "italic", label: "I", title: "Italic", run: (c) => c.toggleItalic(), active: "italic" },
  { name: "underline", label: "U", title: "Underline", run: (c) => c.toggleUnderline(), active: "underline" },
  { name: "strike", label: "S", title: "Strikethrough", run: (c) => c.toggleStrike(), active: "strike" },
  { name: "link", label: "🔗", title: "Link", special: "link", active: "link" },
  { name: "linkcolor", label: "🎨", title: "Link colour", special: "linkcolor" },
  { name: "linkcolorreset", label: "↺", title: "Reset link colour", special: "linkcolorreset" },
  { name: "heading", label: "H", title: "Heading", run: (c) => c.toggleHeading({ level: 1 }), active: "heading" },
  { name: "quote", label: "❝", title: "Quote", run: (c) => c.toggleBlockquote(), active: "blockquote" },
  { name: "code", label: "</>", title: "Code", run: (c) => c.toggleCodeBlock(), active: "codeBlock" },
  { name: "bullets", label: "•", title: "Bullet list", run: (c) => c.toggleBulletList(), active: "bulletList" },
  { name: "numbers", label: "1.", title: "Numbered list", run: (c) => c.toggleOrderedList(), active: "orderedList" },
  { name: "checklist", label: "☑", title: "Checklist", run: (c) => c.toggleTaskList(), active: "taskList" },
  { name: "outdent", label: "⇤", title: "Decrease level", run: (c, editor) => c.liftListItem(listItemType(editor)) },
  { name: "indent", label: "⇥", title: "Increase level", run: (c, editor) => c.sinkListItem(listItemType(editor)) },
  { name: "undo", label: "↶", title: "Undo", run: (c) => c.undo() },
  { name: "redo", label: "↷", title: "Redo", run: (c) => c.redo() }
]

const listItemType = (editor) => (editor.isActive("taskItem") ? "taskItem" : "listItem")

function realtimeUrl(configured) {
  if (configured) return configured
  const secure = window.location.protocol === "https:"
  return secure
    ? `wss://${window.location.host}/realtime`
    : `ws://${window.location.hostname}:${Number(window.location.port || 80) + REALTIME_PORT_OFFSET}`
}

class TiptapEditorElement extends HTMLElement {
  connectedCallback() {
    if (this.editor) return

    this.collaborative = this.dataset.mode === "collab"
    this.#buildDom()
    this.#buildEditor()
  }

  disconnectedCallback() {
    clearTimeout(this.openLinkTimer)
    if (this.provider) {
      providers.delete(this.provider)
      this.provider.destroy()
    }
    this.editor?.destroy()
    dirtyFormEditors.delete(this)
    this.editor = null
  }

  #buildDom() {
    this.toolbar = document.createElement("div")
    this.toolbar.className = "tiptap-toolbar"
    this.toolbar.setAttribute("role", "toolbar")
    this.buttons = new Map()

    TOOLBAR.forEach((definition) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = `tiptap-button tiptap-button--${definition.name}`
      button.textContent = definition.label
      button.title = definition.title
      button.setAttribute("aria-label", definition.title)
      button.addEventListener("mousedown", (event) => event.preventDefault())
      button.addEventListener("click", () => this.#runToolbar(definition))
      this.toolbar.append(button)
      this.buttons.set(definition.name, button)
    })

    this.colorInput = document.createElement("input")
    this.colorInput.type = "color"
    this.colorInput.className = "tiptap-color-input"
    this.colorInput.value = DEFAULT_LINK_COLOR
    this.colorInput.addEventListener("input", () => this.#setLinkColor(this.colorInput.value))
    this.toolbar.append(this.colorInput)

    this.surface = document.createElement("div")
    this.surface.className = "tiptap-surface"

    this.status = document.createElement("div")
    this.status.className = "tiptap-status"
    this.status.setAttribute("aria-live", "polite")

    this.append(this.toolbar, this.surface, this.status)

    if (!this.collaborative) {
      this.input = document.createElement("input")
      this.input.type = "hidden"
      this.input.name = this.dataset.inputName
      this.append(this.input)
    }
  }

  #buildEditor() {
    const extensions = richTextExtensions({ collaborative: this.collaborative })
    let content = ""

    if (this.collaborative) {
      const ydoc = new Y.Doc()
      this.provider = new HocuspocusProvider({
        url: realtimeUrl(this.dataset.realtimeUrl),
        name: this.dataset.documentName,
        token: this.dataset.token,
        document: ydoc
      })
      providers.add(this.provider)
      this.#watchProvider()

      extensions.push(
        Collaboration.configure({ document: ydoc }),
        CollaborationCaret.configure({ provider: this.provider, user: ANONYMOUS })
      )
    } else {
      content = this.dataset.value || ""
      this.input.value = content
    }

    this.editor = new Editor({
      element: this.surface,
      extensions,
      content,
      editorProps: {
        attributes: { class: "tiptap-content trix-content", "aria-label": this.dataset.field },
        handleClick: (_view, _pos, event) => this.#openLinkSoon(event),
        handleDoubleClick: (view, pos) => this.#selectLinkAt(view, pos),
        // Files aren't supported here: swallow drops so the browser doesn't navigate to them.
        handleDrop: (_view, event) => Boolean(event.dataTransfer?.files?.length),
        handlePaste: (_view, event) => Boolean(event.clipboardData?.files?.length)
      },
      onTransaction: ({ editor }) => this.#refreshToolbar(editor),
      onUpdate: () => this.#onContentChanged()
    })

  }

  #watchProvider() {
    const show = (state, text) => {
      this.status.dataset.state = state
      this.status.textContent = text
    }
    show("connecting", "Connecting…")

    this.provider.on("synced", () => show("saved", "All changes saved"))
    this.provider.on("status", ({ status }) => {
      if (status !== "connected") show("offline", "Offline — changes are kept and will sync when reconnected")
    })
    this.provider.on("unsyncedChanges", ({ number }) => {
      if (number > 0) show("saving", "Saving…")
      else if (this.provider.synced) show("saved", "All changes saved")
    })
    this.provider.on("authenticationFailed", () => show("error", "Not allowed to edit this field"))
    this.provider.on("stateless", ({ payload }) => {
      let message
      try { message = JSON.parse(payload) } catch { return }
      if (message.type === "identity") this.editor?.commands.updateUser(message.user)
    })
  }

  #onContentChanged() {
    if (this.collaborative) return

    this.input.value = this.editor.getHTML()
    dirtyFormEditors.add(this)
    this.closest("form")?.addEventListener("submit", () => dirtyFormEditors.delete(this), { once: true })
  }

  // Takes the editor as an argument: the first transaction fires while the
  // Editor constructor is still running, before this.editor is assigned.
  #refreshToolbar(editor) {
    TOOLBAR.forEach((definition) => {
      const button = this.buttons.get(definition.name)
      button.classList.toggle("is-active", Boolean(definition.active && editor.isActive(definition.active)))
    })
    const inLink = editor.isActive("link")
    this.buttons.get("linkcolor").disabled = !inLink
    this.buttons.get("linkcolorreset").disabled = !inLink
    if (inLink) this.colorInput.value = editor.getAttributes("link").color || DEFAULT_LINK_COLOR
    this.buttons.get("undo").disabled = !this.collaborative && !editor.can().undo()
    this.buttons.get("redo").disabled = !this.collaborative && !editor.can().redo()
  }

  #runToolbar(definition) {
    if (definition.special === "link") return this.#editLink()
    if (definition.special === "linkcolor") return this.colorInput.click()
    if (definition.special === "linkcolorreset") return this.#setLinkColor(null)

    definition.run(this.editor.chain().focus(), this.editor).run()
  }

  #openLinkSoon(event) {
    const link = event.target.closest?.("a[href]")
    if (!link || event.button !== 0) return false

    clearTimeout(this.openLinkTimer)
    this.openLinkTimer = setTimeout(() => window.open(link.href, "_blank", "noopener"), DOUBLE_CLICK_GRACE_MS)
    return false
  }

  #setLinkColor(color) {
    this.editor.chain().focus().extendMarkRange("link").updateAttributes("link", { color }).run()
  }

  #selectLinkAt(view, pos) {
    clearTimeout(this.openLinkTimer)

    const range = getMarkRange(view.state.doc.resolve(pos), view.state.schema.marks.link)
    if (!range) return false

    this.editor.commands.setTextSelection(range)
    return true
  }

  // On a link, the prompt is pre-filled with its address: OK updates it,
  // an empty address removes it, Cancel leaves it alone.
  #editLink() {
    const editingExisting = this.editor.isActive("link")
    const current = editingExisting ? this.editor.getAttributes("link").href : ""
    const href = window.prompt(editingExisting ? "Link address (clear to remove)" : "Link address", current)
    if (href === null) return

    const chain = this.editor.chain().focus()
    if (editingExisting) chain.extendMarkRange("link")

    if (href.trim()) chain.setLink({ href: href.trim() }).run()
    else if (editingExisting) chain.unsetLink().run()
  }
}

customElements.define("tiptap-editor", TiptapEditorElement)

// Warn only when edits genuinely have not reached the server yet.
window.addEventListener("beforeunload", (event) => {
  const unsynced = Array.from(providers).some((provider) => provider.unsyncedChanges > 0)
  if (unsynced || dirtyFormEditors.size > 0) {
    event.preventDefault()
    event.returnValue = ""
  }
})
